import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft, Mic, Square, Upload, Loader2, Sparkles, ChevronRight, Trash2,
} from "lucide-react";
import api from "../utils/qccaApi";

const MAX_BYTES = 24 * 1024 * 1024;

export default function QCCAClerkNew() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "",
    meeting_date: new Date().toISOString().slice(0, 10),
    attendees_csv: "",
    agenda: "",
  });
  const [stage, setStage] = useState("setup"); // setup | capture | processing | done
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [progress, setProgress] = useState("");
  const [sessionId, setSessionId] = useState(null);

  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") mediaRecRef.current.stop();
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  const setField = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: mime });
        stream.getTracks().forEach((t) => t.stop());
        if (b.size > MAX_BYTES) {
          toast.error(`Recording is ${Math.round(b.size / 1024 / 1024)} MB — Whisper max is 24 MB. Split longer meetings.`);
          return;
        }
        setBlob(b);
        setBlobUrl(URL.createObjectURL(b));
        setUploadedFile(null);
      };
      mr.start(1000);
      mediaRecRef.current = mr;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Could not access microphone. Check browser permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
      mediaRecRef.current.stop();
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  };

  const pickFile = (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`File is ${Math.round(file.size / 1024 / 1024)} MB — max is 24 MB.`);
      return;
    }
    setUploadedFile(file);
    setBlob(file);
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(URL.createObjectURL(file));
  };

  const discardAudio = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null); setBlobUrl(null); setUploadedFile(null); setElapsed(0);
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60); const ss = s % 60;
    return `${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  };

  const processAll = async () => {
    if (!form.title.trim()) { toast.error("Give your meeting a title."); return; }
    if (!blob) { toast.error("Record or upload audio first."); return; }

    setStage("processing");
    try {
      // 1. Create session
      setProgress("Creating session…");
      const attendees = form.attendees_csv.split(",").map((a) => a.trim()).filter(Boolean);
      const sessionRes = await api.post("/qcca/clerk/sessions", {
        title: form.title.trim(),
        meeting_date: form.meeting_date || null,
        attendees,
        agenda: form.agenda.trim() || null,
      });
      const sid = sessionRes.data.session.id;
      setSessionId(sid);

      // 2. Upload audio
      setProgress("Uploading audio…");
      const fd = new FormData();
      const filename = uploadedFile?.name || `recording-${Date.now()}.webm`;
      fd.append("audio", blob, filename);
      await api.post(`/qcca/clerk/sessions/${sid}/audio`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // 3. Transcribe
      setProgress("Transcribing with Whisper… (this may take 10–60s)");
      await api.post(`/qcca/clerk/sessions/${sid}/transcribe`);

      // 4. Structure into minutes
      setProgress("Drafting meeting minutes…");
      await api.post(`/qcca/clerk/sessions/${sid}/minutes`);

      setStage("done");
      toast.success("Minutes are ready.");
      // Navigate after a brief pause so the user sees the success state
      setTimeout(() => nav(`/qcca/boardroom/clerk/${sid}`), 700);
    } catch (e) {
      setStage("capture");
      toast.error(e?.response?.data?.detail || "Processing failed. Please try again.");
    }
  };

  const inputCls = "w-full bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-white placeholder-gray-600 font-mono text-sm px-4 py-3 outline-none transition-colors";

  if (stage === "processing" || stage === "done") {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-[#0a0a0a] border border-[#1a1a1a] p-10 text-center" data-testid="qcca-clerk-processing">
          <div className="w-14 h-14 mx-auto flex items-center justify-center border border-[#C5A52A]/50 text-[#C5A52A]">
            {stage === "done" ? <Sparkles className="w-6 h-6" /> : <Loader2 className="w-6 h-6 animate-spin" />}
          </div>
          <h2 className="mt-6 font-headline text-2xl text-white">
            {stage === "done" ? "Minutes ready." : "Working on it…"}
          </h2>
          <p className="mt-3 font-mono text-xs text-gray-400 uppercase tracking-[0.22em]">{progress}</p>
          <p className="mt-6 text-gray-500 text-xs">Whisper handles the transcription; Claude structures the minutes. You can edit everything before saving.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/qcca/boardroom/clerk" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 hover:text-[#C5A52A] mb-6">
          <ArrowLeft className="w-3 h-3" /> Clerk
        </Link>

        <div className="mb-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Digital Clerk</div>
          <h1 className="mt-3 font-headline text-4xl text-white leading-tight">New <span className="italic text-[#C5A52A]">meeting.</span></h1>
          <p className="mt-3 text-gray-400 leading-relaxed">
            Fill in the basics, record the meeting (or upload an existing file), and the Clerk will transcribe and draft your minutes. Max 24 MB per recording.
          </p>
        </div>

        <div className="bg-[#0a0a0a] border border-[#1a1a1a] p-6 sm:p-10 space-y-6" data-testid="qcca-clerk-new-form">
          {/* Metadata */}
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">Meeting title *</label>
            <input value={form.title} onChange={setField("title")} className={inputCls} placeholder="e.g. Regular Board Meeting — April 2026" data-testid="qcca-clerk-title" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">Date</label>
              <input type="date" value={form.meeting_date} onChange={setField("meeting_date")} className={inputCls} data-testid="qcca-clerk-date" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">Attendees (comma-separated, optional)</label>
              <input value={form.attendees_csv} onChange={setField("attendees_csv")} className={inputCls} placeholder="Jane, John, Priya" data-testid="qcca-clerk-attendees" />
            </div>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">Agenda (optional — helps the AI)</label>
            <textarea rows={3} value={form.agenda} onChange={setField("agenda")} className={`${inputCls} resize-none`} placeholder="1. Approve last month's minutes  2. Reserve-fund update  3. Snow removal contract" data-testid="qcca-clerk-agenda" />
          </div>

          {/* Capture */}
          <div className="pt-6 border-t border-[#1a1a1a]">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-4">Audio</div>

            {!blob ? (
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Record */}
                <button type="button" onClick={recording ? stopRecording : startRecording}
                  className={`flex flex-col items-center justify-center gap-3 p-8 border transition-colors ${
                    recording ? "border-red-500 text-red-400 bg-red-500/5" : "border-[#1a1a1a] text-gray-300 hover:border-[#C5A52A] hover:text-[#C5A52A]"
                  }`}
                  data-testid="qcca-clerk-record-btn">
                  {recording ? <Square className="w-8 h-8" /> : <Mic className="w-8 h-8" />}
                  <div className="font-headline text-lg">
                    {recording ? `Stop · ${formatTime(elapsed)}` : "Record in browser"}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">
                    {recording ? "Tap to finish" : "Uses your microphone"}
                  </div>
                </button>

                {/* Upload */}
                <label className="flex flex-col items-center justify-center gap-3 p-8 border border-[#1a1a1a] text-gray-300 hover:border-[#C5A52A] hover:text-[#C5A52A] cursor-pointer transition-colors"
                  data-testid="qcca-clerk-upload-label">
                  <Upload className="w-8 h-8" />
                  <div className="font-headline text-lg">Upload a file</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">MP3 · WAV · M4A · WebM · ≤24 MB</div>
                  <input type="file" accept="audio/*,.mp3,.wav,.m4a,.webm,.ogg,.mp4" className="hidden"
                    onChange={(e) => pickFile(e.target.files?.[0])}
                    data-testid="qcca-clerk-upload-input" />
                </label>
              </div>
            ) : (
              <div className="border border-[#C5A52A]/40 bg-[#C5A52A]/5 p-4 flex items-center gap-4" data-testid="qcca-clerk-audio-ready">
                <audio controls src={blobUrl} className="flex-1" />
                <div className="font-mono text-[10px] text-gray-400 uppercase tracking-[0.22em] whitespace-nowrap">
                  {Math.round(blob.size / 1024)} KB
                </div>
                <button type="button" onClick={discardAudio}
                  className="inline-flex items-center gap-1 border border-[#1a1a1a] hover:border-red-500 text-gray-400 hover:text-red-400 font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 transition-colors"
                  data-testid="qcca-clerk-discard-audio">
                  <Trash2 className="w-3 h-3" /> Discard
                </button>
              </div>
            )}
          </div>

          <button onClick={processAll} disabled={!blob || !form.title.trim()}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#C5A52A] disabled:bg-[#1a1a1a] disabled:text-gray-500 hover:bg-[#D4B93A] text-black font-mono text-xs uppercase tracking-[0.22em] px-6 py-4 transition-colors"
            data-testid="qcca-clerk-process-btn">
            <Sparkles className="w-4 h-4" /> Transcribe &amp; draft minutes
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
